import { useConnection } from "@solana/wallet-adapter-react";
import { useWallet } from "@solana/wallet-adapter-react";

import { useCallback, useState } from "react";
import { PublicKey, Transaction } from "@solana/web3.js";
import * as bs58 from "bs58";
import {
    getAssociatedTokenAddress,
    createAssociatedTokenAccountInstruction,
    createTransferInstruction,
    TOKEN_PROGRAM_ID,
    AccountLayout,
} from "@solana/spl-token";

import InsufficientFundError from "../Errors/InsufficientFundError";
import NftNotAvailableError from "../Errors/NftNotAvailableError";
import AbnormalError from "../Errors/AbnormalError";
import { useAuth } from "../Contexts/useAuth";
import { useMetamaskWallet } from "../Contexts/useMetamaskWallet";
import { SolanaTransferContext } from "../Contexts/useSolanaTransfer";
import {
    saveSwappingRecords,
    updateSwappingRecords,
} from "../Services/ApiClient";

import config from "../config.json";

const SOLANA_COLD_WALLET = new PublicKey(config["SolanaColdWallet"]);
const SOLANA_TOKEN_ACCOUNT_USIZE = 165;

async function checkAssociatedTokenAccount(
    associatedTokenAddress,
    connection,
    owner,
    mint
) {
    const info = await connection.getAccountInfo(
        associatedTokenAddress,
        "confirmed"
    );
    if (info === null) {
        console.error("ATA Not Found");
        return false;
    }
    if (!info.owner.equals(TOKEN_PROGRAM_ID)) {
        console.error("Not ATA");
        return false;
    }
    if (info.data.length !== AccountLayout.span) {
        console.error("Invalid ATA Data");
        return false;
    }
    const rawAccount = AccountLayout.decode(Buffer.from(info.data));
    if (!rawAccount.mint.equals(mint)) {
        console.error("Mint Not Matched");
        return false;
    }
    if (!rawAccount.owner.equals(owner)) {
        console.error("Owner Not Matched");
        return false;
    }

    return true;
}

async function getNFTOwnerAcct(connection, owner, mint) {
    const promises = [];
    // Get Normal ATA
    promises.push(getAssociatedTokenAddress(mint, owner));
    // Get ATA via RPC call
    promises.push(
        connection
            .getTokenAccountsByOwner(owner, {
                mint,
            })
            .then((result) => result.value[0].pubkey)
    );
    // Get NFT Owner Acct
    promises.push(
        connection
            .getTokenLargestAccounts(mint)
            .then((result) => result.value[0].address)
    );

    const [normalATA, ownerATA, largestATA] = await Promise.all(promises);

    if (ownerATA.toBase58() !== largestATA.toBase58()) {
        throw new NftNotAvailableError(
            "Current NFT Owner is not the largest token account",
            largestATA,
            mint
        );
    }

    console.log("normalATA", normalATA.toBase58());
    console.log("ownerATA", ownerATA.toBase58());

    return ownerATA;
}

export function SolanaTransferProvider({ children }) {
    const { connection } = useConnection();
    const { publicKey: sender, signAllTransactions } = useWallet();
    const { currentVerifiedPolygonPubKey: polygonPubKey } = useMetamaskWallet();
    const [transferResult, setTransferResult] = useState([]);
    const { jwt } = useAuth();
    const transfer = useCallback(
        async (nfts) => {
            try {
                if (jwt === "") {
                    throw new AbnormalError("Not Authed");
                }
                if (polygonPubKey === "") {
                    throw new AbnormalError("No verified Polygon address");
                }

                if (sender === null || connection === null) {
                    throw new AbnormalError("No wallet connected");
                }
                const nftTokenAddrTxSigs = []; // Sent to Server
                const failedNftTokenAddrTxSigs = []; // Sent to Server
                const transferResult = []; // For FE Render
                const preparedData = await Promise.all(
                    nfts.map(async (nft) => {
                        const mint = new PublicKey(nft["token_address"]);
                        const fromTokenAccount = await getNFTOwnerAcct(
                            connection,
                            sender,
                            mint
                        );
                        const toTokenAccount = await getAssociatedTokenAddress(
                            mint,
                            SOLANA_COLD_WALLET
                        );
                        console.log("recipientATA", toTokenAccount.toBase58());
                        const result = await checkAssociatedTokenAccount(
                            toTokenAccount,
                            connection,
                            SOLANA_COLD_WALLET,
                            mint
                        );
                        return {
                            nft,
                            mint,
                            fromTokenAccount,
                            toTokenAccount,
                            ataExisted: result,
                        };
                    })
                );
                const { blockhash } = await connection.getLatestBlockhash();
                const txs = [];
                let tx;
                preparedData.forEach((data) => {
                    tx = new Transaction();
                    tx.feePayer = sender;
                    tx.recentBlockhash = blockhash;
                    txs.push(tx);
                    if (!data.ataExisted) {
                        tx.add(
                            createAssociatedTokenAccountInstruction(
                                sender,
                                data.toTokenAccount,
                                SOLANA_COLD_WALLET,
                                data.mint
                            )
                        );
                        tx.hasAtaInstruction = true;
                    } else {
                        tx.hasAtaInstruction = false;
                    }
                    tx.add(
                        createTransferInstruction(
                            data.fromTokenAccount,
                            data.toTokenAccount,
                            sender,
                            1
                        )
                    );
                });

                const estimatedFees = await Promise.all(
                    [].concat(
                        txs.map(async (tx) => {
                            let fee = 0;
                            fee += await tx.getEstimatedFee(connection);
                            return fee;
                        }),
                        txs.map(async (tx) => {
                            let fee = 0;
                            if (tx.hasAtaInstruction) {
                                fee +=
                                    await connection.getMinimumBalanceForRentExemption(
                                        SOLANA_TOKEN_ACCOUNT_USIZE
                                    );
                            }
                            return fee;
                        })
                    )
                );

                const estimatedTotalFee = estimatedFees.reduce(
                    (acc, cur) => acc + cur,
                    0
                );
                console.log("Calculated Fees:", estimatedTotalFee);

                const userBalance = await connection.getBalance(sender);
                console.log("User Balance:", userBalance);

                if (userBalance < estimatedTotalFee) {
                    throw new InsufficientFundError(
                        "Insufficient funds for fee",
                        estimatedTotalFee,
                        userBalance
                    );
                }

                const signedTxs = await signAllTransactions(txs);
                signedTxs.forEach((signedTx, indx) => {
                    const txSig = bs58.encode(signedTx.signature);
                    const nftTokenAddr =
                        preparedData[indx].nft["token_address"];
                    const nftName = preparedData[indx].nft["token_name"];
                    const tokenAddrTxSigItem = {
                        nftTokenAddr,
                        txSig,
                    };
                    const resultItem = {
                        NFTTokenAddr: nftTokenAddr,
                        NFTName: nftName,
                        SolanaTxSig: txSig,
                    };
                    nftTokenAddrTxSigs.push(tokenAddrTxSigItem);
                    transferResult.push(resultItem);
                });
                const { recordGroupId, size } = await saveSwappingRecords(
                    jwt,
                    polygonPubKey,
                    nfts.map((nft) => nft["token_address"])
                );
                if (size !== nfts.length) {
                    throw new AbnormalError(
                        "NFTs submitted not match the size of saved records"
                    );
                }

                console.log(
                    "After Sign Txs:",
                    nftTokenAddrTxSigs,
                    transferResult
                );

                const sentResults = await Promise.allSettled(
                    signedTxs.map((signedTx) =>
                        connection.sendRawTransaction(signedTx.serialize())
                    )
                );

                sentResults.forEach((result, indx) => {
                    const resultItem = transferResult[indx];
                    if (result.status === "rejected") {
                        resultItem.Status = "failed";
                        resultItem.FailedReason = result?.reason?.message || "";
                        const [failed] = nftTokenAddrTxSigs.splice(indx, 1);
                        failed.reason = result?.reason?.message || "";
                        failedNftTokenAddrTxSigs.push(failed);
                    } else {
                        console.log(
                            "After Sent:",
                            result.value,
                            resultItem.SolanaTxSig
                        );
                        resultItem.Status = "sent";
                    }
                });

                if (
                    nftTokenAddrTxSigs.length > 0 ||
                    failedNftTokenAddrTxSigs.length > 0
                ) {
                    await updateSwappingRecords(
                        jwt,
                        recordGroupId,
                        nftTokenAddrTxSigs,
                        failedNftTokenAddrTxSigs
                    );
                }

                setTransferResult(transferResult);
                return true;
            } catch (error) {
                // Any Abnormal Error is not normal case & should not be handled here, just ignore them.
                if (error instanceof AbnormalError) {
                    console.error(error);
                    // Do nothing
                    return false;
                }

                setTransferResult([]);
                throw error;
            }
        },
        [jwt, connection, sender, polygonPubKey, signAllTransactions]
    );

    return (
        <SolanaTransferContext.Provider value={{ transfer, transferResult }}>
            {children}
        </SolanaTransferContext.Provider>
    );
}
